/* eslint-disable */
import { useState, useEffect } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import { useTranslation } from "react-i18next";

/*------ import url and method ------*/
import * as url from "helper/UrlHelper";
import { getData, putData } from "utils/Gateway";

import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import { productDetailsHeaderLinks } from "helper/ProductHelper/ProductHelper";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";

const ProductCategoriesBody = () => {
  const { t } = useTranslation();

  const params = useParams();
  const history = useHistory();

  const commonHeaderObject = productDetailsHeaderLinks(params.id, t);

  const token = localStorage.getItem("token");

  const variantTemplate = {
    name: "",
    optionValue: null,
    options: [],
  };

  const [variantBlock, setVariantBlock] = useState([]);
  const [deletedVariantBlock, setDeletedVariantBlock] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // Alert requirements
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const validateForm = () => {
    const newErrors = variantBlock.map((block) => {
      const blockErrors = {};
      if (!block.name.trim()) {
        blockErrors.name = "Attribute name is required.";
      }
      if (!block.options || block.options.length === 0) {
        blockErrors.options = "Please add at least one variation.";
      }
      return blockErrors;
    });

    setErrors(newErrors);
    return newErrors.every((err) => Object.keys(err).length === 0);
  };

  const addTemplateBlock = () => {
    if (validateForm()) {
      setVariantBlock([...variantBlock, { ...variantTemplate }]);
      setErrors([...errors, {}]);
    }
  };

  const onDeleteBlock = (index, block) => {
    if (variantBlock.length === 1) {
      setAlertMessage("You must have at least one product variation.");
      setMessageType("error");
      setShowAlert(true);
      return;
    }

    const filteredBlock = [...variantBlock];
    filteredBlock.splice(index, 1);
    setVariantBlock(filteredBlock);

    const filteredErrors = [...errors];
    filteredErrors.splice(index, 1);
    setErrors(filteredErrors);

    if (block._id) {
      setDeletedVariantBlock((prev) => [...prev, block._id]);
    }
  };

  const changeInputs = (index, field, value) => {
    const updatedFormValues = variantBlock.map((block, i) =>
      index === i ? { ...block, [field]: value } : block
    );
    setVariantBlock(updatedFormValues);

    if (errors[index]?.[field] && value.trim()) {
      const newErrors = [...errors];
      delete newErrors[index][field];
      setErrors(newErrors);
    }
  };

  const changeInputSelect = (index, optionValueField, optionsField, value) => {
    const updatedFormValues = variantBlock.map((block, i) =>
      index === i
        ? {
            ...block,
            [optionValueField]: value,
            [optionsField]: value.map((v) => v.value),
          }
        : block
    );
    setVariantBlock(updatedFormValues);

    if (errors[index]?.[optionsField] && value.length > 0) {
      const newErrors = [...errors];
      delete newErrors[index][optionsField];
      setErrors(newErrors);
    }
  };

  const getProductDetails = async () => {
    try {
      let requestUrl = `${url.API_BASE_URL}${url.API_GET_PRODUCT_DETAILS}/${params.id}?token=${token}`;
      const response = await getData(requestUrl);

      if (response.status) {
        const data = response.data;
        if (data.productvarations && data.productvarations.length > 0) {
          const updatedProductVariations = data.productvarations.map(
            (productVariation) => ({
              _id: productVariation._id,
              name: productVariation.name,
              options: productVariation.options,
              optionValue: productVariation.options.map((option) => ({
                label: option,
                value: option,
              })),
            })
          );
          setVariantBlock(updatedProductVariations);
          setErrors(updatedProductVariations.map(() => ({})));
        } else {
          setVariantBlock([variantTemplate]);
          setErrors([{}]);
        }
      } else {
        setAlertMessage(response.message || "Failed to fetch product details.");
        setMessageType("error");
        setShowAlert(true);
      }
    } catch (error) {
      setAlertMessage(error.message || "An unexpected error occurred.");
      setMessageType("error");
      setShowAlert(true);
    }
  };

  const saveProductVariantHandler = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);
      const productData = {
        variations: variantBlock.map((block) => ({
          _id: block._id || null,
          name: block.name,
          options: block.options,
        })),
        deletedvariations: deletedVariantBlock,
      };

      let requestUrl = `${url.API_BASE_URL}${url.API_UPDATE_PRODUCT}/${params.id}?token=${token}`;
      const response = await putData(requestUrl, productData);

      if (response.status) {
        setDeletedVariantBlock([]);
        setAlertMessage(
          response.message || "Product variations saved successfully!"
        );
        setMessageType("success");
        setTimeout(() => {
          getProductDetails();
        }, 1000);
      } else {
        setAlertMessage(
          response.message || "Failed to save product variations."
        );
        setMessageType("error");
      }
    } catch (error) {
      setAlertMessage(
        error.message || "An unexpected error occurred while saving."
      );
      setMessageType("error");
    } finally {
      setIsSaving(false);
      setShowAlert(true);
    }
  };

  useEffect(() => {
    if (params.id) {
      getProductDetails();
    } else {
      setVariantBlock([variantTemplate]);
      setErrors([{}]);
    }
  }, [params.id]);

  const onAlertClose = () => {
    setShowAlert(false);
    setAlertMessage("");
    setMessageType("");
  };

  const customStyles = (hasError) => ({
    control: (base) => ({
      ...base,
      backgroundColor: "var(--bs-white)",
      borderColor: hasError ? "var(--bs-danger)" : "#ced4da",
      "&:hover": {
        borderColor: hasError ? "var(--bs-danger)" : "#adb5bd",
      },
      borderRadius: "7px",
      padding: "3px",
      minHeight: "40px",
      boxShadow: "none",
    }),
    placeholder: (base) => ({ ...base, color: "#6c757d" }),
    menu: (base) => ({ ...base, zIndex: 3 }),
  });

  return (
    <div id="content_wrapper">
      <section className="marketplace-wrapper bg-white">
        {params.id && (
          <TabsHeader
            commonHeaderObject={commonHeaderObject}
            activeOption={t("Variants")}
          />
        )}

        <div className="container-fluid px-lg-5 py-4">
          <div className="p-4 p-lg-4 border rounded-3 shadow-sm">
            <h4 className="fw-bold mb-4">Product Variations</h4>
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="d-flex flex-column gap-3">
                {variantBlock.map((block, index) => {
                  const blockErrors = errors[index] || {};
                  return (
                    <div className="p-3 border rounded-3 bg-light" key={index}>
                      <div className="row align-items-start">
                        <div className="col-11">
                          <div className="row">
                            <div className="col-lg-4">
                              <label
                                htmlFor={`attributeName-${index}`}
                                className="form-label fw-semibold"
                              >
                                Attribute Name
                              </label>
                              <input
                                type="text"
                                id={`attributeName-${index}`}
                                className={`form-control ${
                                  blockErrors.name
                                    ? "is-invalid"
                                    : "border border-gray-300"
                                }`}
                                placeholder="e.g., Size or Color"
                                value={block.name}
                                onChange={(e) =>
                                  changeInputs(index, "name", e.target.value)
                                }
                              />
                              {blockErrors.name && (
                                <div className="invalid-feedback">
                                  {blockErrors.name}
                                </div>
                              )}
                            </div>
                            <div className="col-lg-8">
                              <label
                                htmlFor={`variations-${index}`}
                                className="form-label fw-semibold"
                              >
                                Variations
                              </label>
                              <CreatableSelect
                                id={`variations-${index}`}
                                placeholder="e.g., Small, Medium, Large"
                                styles={customStyles(!!blockErrors.options)}
                                isMulti
                                value={block.optionValue}
                                onChange={(value) =>
                                  changeInputSelect(
                                    index,
                                    "optionValue",
                                    "options",
                                    value
                                  )
                                }
                              />
                              {blockErrors.options && (
                                <div className="text-danger mt-1 fs-sm">
                                  {blockErrors.options}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-1 d-flex justify-content-center">
                          <button
                            type="button"
                            className="btn btn-gray border border-gray-300"
                            onClick={() => onDeleteBlock(index, block)}
                            title="Delete Attribute"
                            disabled={variantBlock.length === 1}
                            style={{
                              padding: "0.685rem 0.75rem",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginTop: "32px", // Aligns button with inputs
                            }}
                          >
                            <span className="material-symbols-outlined">
                              delete
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between mt-4">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={addTemplateBlock}
                >
                  <span className="material-symbols-outlined me-1 align-middle">
                    add
                  </span>
                  Add Another Attribute
                </button>
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  onClick={saveProductVariantHandler}
                  disabled={isSaving}
                  style={{ minWidth: "150px" }}
                >
                  {isSaving ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Saving...
                    </>
                  ) : (
                    "Save Variations"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {showAlert && (
          <AlertNotification
            showAlert={showAlert}
            message={alertMessage}
            alertType={messageType}
            onClose={onAlertClose}
          />
        )}
      </section>
    </div>
  );
};

export default ProductCategoriesBody;
